import { useEffect, useState } from 'react';
import { configAPI } from '../utils/api';

// Runtime config from GET /api/config: feature flags and the live
// subscription price (AD4). Fetched once per page load and shared.
const DEFAULTS = {
  features: { programTypes: false, sms: false, push: false, events: false, ledger: false, offline: false, waivers: false, rsvpChase: false, chat: false, signups: false, scoreboard: false, albums: false, orgs: false, help: false, handout: false },
  subscription: null, // unknown until loaded — never assume a price
  legal: null, // v2.5 AD11: { termsVersion, privacyVersion } from the server constants
};

let cached = null;
let inflight = null;
const listeners = new Set();

const load = () => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    // Started inside a promise so a synchronous throw also falls back to DEFAULTS.
    inflight = Promise.resolve().then(() => configAPI.get())
      .then((res) => {
        cached = { ...DEFAULTS, ...(res.data || {}) };
        listeners.forEach((fn) => fn(cached));
        return cached;
      })
      .catch(() => {
        inflight = null;
        return DEFAULTS;
      });
  }
  return inflight;
};

export const formatPrice = (subscription, { perMonth = true } = {}) => {
  if (!subscription || typeof subscription.amountCents !== 'number') return perMonth ? '$—/month' : '$—';
  const dollars = subscription.amountCents / 100;
  const amount = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  if (!perMonth) return amount;
  const unit = subscription.interval === 'year' ? 'year' : 'month';
  return `${amount}/${unit}`;
};

export default function useAppConfig() {
  const [config, setConfig] = useState(cached || DEFAULTS);

  useEffect(() => {
    let active = true;
    listeners.add(setConfig);
    load().then((c) => { if (active) setConfig(c); });
    return () => { active = false; listeners.delete(setConfig); };
  }, []);

  return {
    ...config,
    loaded: !!cached,
    price: formatPrice(config.subscription),
    priceAmount: formatPrice(config.subscription, { perMonth: false }),
  };
}

// Test hook: forget the cached config.
export const __resetAppConfigCache = () => { cached = null; inflight = null; };
